<template>
  <div class="dashboard-container">
    <h2 class="page-title">{{ $t('userInfo.title') }}</h2>
    
    <!-- User Information Section -->
    <div class="user-info" v-if="user">
      <h3 class="subtitle">{{ $t('userProfile.personalInfo') }}</h3>
      <div class="info-grid">
        <div class="info-item">
          <label>{{ $t('userProfile.firstName') }} {{ $t('userProfile.lastName').toLowerCase() }}:</label>
          <span>{{ user.first_name }} {{ user.last_name }}</span>
        </div>
        <div class="info-item">
          <label>{{ $t('auth.username') }}:</label>
          <span>{{ user.username }}</span>
        </div>
        <div class="info-item">
          <label>{{ $t('userProfile.email') }}:</label>
          <span>{{ user.email }}</span>
        </div>
        <div class="info-item">
          <label>{{ $t('userProfile.role') }}:</label>
          <span class="role-badge" :class="user.role">{{ $t('roles.' + user.role) }}</span>
        </div>
        <div class="info-item" v-if="user.phone">
          <label>{{ $t('userProfile.phone') }}:</label>
          <span>{{ user.phone }}</span>
        </div>
      </div>
    </div>

    <!-- Payment Summary Cards -->
    <div class="payment-summary" v-if="user">
      <h3 class="sub-title">{{ $t('userInfo.monthlySalary') }}</h3>
      <div class="payment-cards">
        <div class="payment-card salary">
          <div class="card-icon">💰</div>
          <div class="card-content">
            <div class="card-label">{{ $t('userProfile.monthlySalary') }}</div>
            <div class="card-value">SEK {{ Math.round(baseSalaryForMonth).toLocaleString() }}</div>
          </div>
        </div>
        
        <div class="payment-card ob-hours">
          <div class="card-icon">⏰</div>
          <div class="card-content">
            <div class="card-label">{{ $t('userInfo.totalOB') }}</div>
            <div class="card-value">{{ totalOBHours.toFixed(2) }}h</div>
            <div class="card-detail">
              {{ $t('pdf.ob1') }}: {{ currentMonthOB.ob1_hours.toFixed(2) }}h | 
              {{ $t('pdf.ob2') }}: {{ currentMonthOB.ob2_hours.toFixed(2) }}h | 
              {{ $t('pdf.ob3') }}: {{ currentMonthOB.ob3_hours.toFixed(2) }}h
            </div>
          </div>
        </div>
        
        <div class="payment-card ob-bonus">
          <div class="card-icon">💸</div>
          <div class="card-content">
            <div class="card-label">{{ $t('userInfo.obHours') }}</div>
            <div class="card-value">SEK {{ Math.round(monthlyOBBonus).toLocaleString() }}</div>
          </div>
        </div>
        
        <div class="payment-card highlighted breakdown">
          <div class="subcard">
            <div class="card-icon">💵</div>
            <div class="card-content">
              <div class="card-label">{{ $t('userManagement.grossSalary') }}</div>
              <div class="card-value">SEK {{ Math.round(grossSalary).toLocaleString() }}</div>
            </div>
          </div>
          <div class="subcard">
          <div class="card-icon">🏦</div>
          <div class="card-content">
            <div class="card-label">{{ $t('userManagement.employeeTax') }}</div>
            <div class="card-value">SEK {{ Math.round(employeeTax).toLocaleString() }}</div>
          </div>
          </div>
          <div class="subcard">
            <div class="card-icon">✅</div>
            <div class="card-content">
              <div class="card-label">{{ $t('userProfile.netSalary') }}</div>
              <div class="card-value">SEK {{ Math.round(netSalary).toLocaleString() }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="!user && !error" class="loading">
      {{ $t('common.loading') }}
    </div>

    <!-- Error state -->
    <div v-if="error" class="error">
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useShiftStore } from '../stores/shift.js'
import { usePayrollStore } from '../stores/payroll.js'

const user = ref(null)
const error = ref('')
const router = useRouter()
const authStore = useAuthStore()
const shiftStore = useShiftStore()
const payrollStore = usePayrollStore()
// Computed properties for salary and payments (with reactive dependencies)
const currentMonthOB = computed(() => {
  if (!user.value?.id) return { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0 }
  
  // Explicit dependency on payroll store's selected month for reactivity
  const currentMonth = payrollStore.selectedMonth
  const obHours = payrollStore.getUserOBHours(user.value.id)
  
  return obHours
})

const totalOBHours = computed(() => {
  const ob = currentMonthOB.value
  return parseFloat(ob.ob1_hours || 0) + parseFloat(ob.ob2_hours || 0) + parseFloat(ob.ob3_hours || 0)
})

const monthlyOBBonus = computed(() => {
  // OB bonus is already included in the gross salary calculation
  const ob = currentMonthOB.value
  const company = user.value?.company || authStore.company
  const coeffs = {
    ob1: Number(company?.overtime_rate1) ?? 0.3,
    ob2: Number(company?.overtime_rate2) ?? 0.5,
    ob3: Number(company?.overtime_rate3) ?? 1.0,
  }
  
  if (!user.value?.payroll?.hourly_salary) return 0
  const hourlyRate = parseFloat(user.value.payroll.hourly_salary)
  
  return (parseFloat(ob.ob1_hours || 0) * hourlyRate * coeffs.ob1) +
         (parseFloat(ob.ob2_hours || 0) * hourlyRate * coeffs.ob2) +
         (parseFloat(ob.ob3_hours || 0) * hourlyRate * coeffs.ob3)
})

const baseSalaryForMonth = computed(() => {
  if (!user.value?.id) return 0
  
  // Explicit dependency on payroll store's selected month for reactivity
  const currentMonth = payrollStore.selectedMonth
  const baseSalary = payrollStore.getUserBaseSalary(user.value.id)
  
  return baseSalary
})

const grossSalary = computed(() => {
  if (!user.value?.id) return 0
  
  // Explicit dependency on payroll store's selected month for reactivity
  const currentMonth = payrollStore.selectedMonth
  const gross = payrollStore.getUserGrossSalary(user.value.id)
  
  return gross
})

const calculatedTax = ref(0)

const employeeTax = computed(() => {
  if (!user.value?.id) return 0
  
  // Explicit dependency on payroll store's selected month for reactivity
  const currentMonth = payrollStore.selectedMonth
  const tax = payrollStore.getUserEmployeeTax(user.value.id)
  
  return tax
})

const netSalary = computed(() => {
  if (!user.value?.id) return 0
  
  // Explicit dependency on payroll store's selected month for reactivity
  const currentMonth = payrollStore.selectedMonth
  const net = payrollStore.getUserNetSalary(user.value.id)
  
  return net
})

// Fetch user data on component mount
onMounted(async () => {
  await fetchUserData()
  
  // Load payroll data using centralized store
  if (user.value) {
    // Sync with current date from shiftStore (if TimeReporting has set it)
    if (shiftStore.currentDate) {
      await payrollStore.setSelectedMonth(shiftStore.currentDate, [user.value])
    } else {
      // Default to current month if no date is set
      await payrollStore.loadMonthlyData([user.value])
    }
  }
  
  // Listen for events to update salary in real-time
  window.addEventListener('sick-leave-reported', handleSickLeaveReported)
  window.addEventListener('time-entry-saved', handleTimeEntrySaved)
})

// Cleanup event listeners on unmount
onUnmounted(() => {
  window.removeEventListener('sick-leave-reported', handleSickLeaveReported)
  window.removeEventListener('time-entry-saved', handleTimeEntrySaved)
})

// Handle sick leave reported event
async function handleSickLeaveReported(event) {
  const { workerId } = event.detail
  // Only reload if it's for the current user
  if (workerId === user.value?.id && user.value) {
    await payrollStore.loadMonthlyData([user.value], true) // Force reload
  }
}

// Handle time entry saved event
async function handleTimeEntrySaved(event) {
  const { workerId } = event.detail
  // Only reload if it's for the current user
  if (workerId === user.value?.id && user.value) {
    await payrollStore.loadMonthlyData([user.value], true) // Force reload
  }
}

// Watch for changes in gross salary (now handled by payroll store)
watch(grossSalary, () => {
  // Tax calculation is now handled by the payroll store
})

// Watch for changes in the selected month from TimeReporting
watch(() => shiftStore.currentDate, async (newDate, oldDate) => {
  if (newDate && user.value && newDate !== oldDate) {
    // Update payroll store's selected month to match
    await payrollStore.setSelectedMonth(newDate, [user.value])
  }
}, { deep: true, immediate: false })

// Also watch for direct changes in payroll store's selected month
watch(() => payrollStore.selectedMonth, async (newMonth, oldMonth) => {
  if (newMonth && user.value && newMonth !== oldMonth) {
    // Ensure data is loaded for the new month
    await payrollStore.loadMonthlyData([user.value], true)
  }
}, { deep: true, immediate: false })

async function fetchUserData() {
  try {
    if (!authStore.isAuthenticated || !authStore.user) {
      router.push('/')
      return
    }

    // Fetch complete user data from API to ensure we have payroll info
    const response = await authStore.makeAuthenticatedRequest(
      `/api/users/${authStore.user.id}/`
    )
    if (response.ok) {
      user.value = await response.json()
      // If payroll is null, fetch it separately
      if (!user.value.payroll) {
        try {
          // Workers: call without parameters (backend filters automatically)
          // Managers: can use ?user={id} parameter to query specific user
          const payrollUrl = authStore.user.role === 'manager' 
            ? `/api/payroll/?user=${user.value.id}`
            : `/api/payroll/`
          const payrollResponse = await authStore.makeAuthenticatedRequest(payrollUrl)
          if (payrollResponse.ok) {
            const payrollData = await payrollResponse.json()
            // Check if response is array or single object
            if (Array.isArray(payrollData) && payrollData.length > 0) {
              user.value.payroll = payrollData[0]
            } else if (payrollData && !Array.isArray(payrollData)) {
              user.value.payroll = payrollData
            }
          }
        } catch (payrollError) {
          console.warn('Could not fetch payroll:', payrollError)
        }
      }
    } else {
      throw new Error(`HTTP ${response.status}`)
    }
  } catch (err) {
    error.value = err.message || 'Failed to load user data'
    console.error('Error fetching user data:', err)
  }
}

// OLD FUNCTION - OB hours loading now handled by centralized payroll store
// async function loadMonthlyOBHours() {
//   try {
//     if (!user.value?.id) return
//     
//     // Use the current date from shiftStore (set by TimeReporting)
//     const currentDate = shiftStore.currentDate || new Date()
//     const year = currentDate.getFullYear()
//     const month = currentDate.getMonth() + 1 // JavaScript months are 0-indexed
//     
//     // Get monthly OB hours data from API with userId
//     const obData = await obHoursApi.getMonthlyOBHours(user.value.id, year, month)
//     
//     // Use the aggregated monthly total if available
//     const userOBTotals = {}
//     if (obData.monthlyTotal) {
//       userOBTotals[user.value.id] = obData.monthlyTotal
//     } else if (obData.results && Array.isArray(obData.results)) {
//       // Fallback: manually aggregate if monthlyTotal not present
//       const totals = obData.results.reduce((acc, day) => {
//         return {
//           ob1_hours: acc.ob1_hours + (day.ob1_hours || 0),
//           ob2_hours: acc.ob2_hours + (day.ob2_hours || 0),
//           ob3_hours: acc.ob3_hours + (day.ob3_hours || 0),
//           total_hours: acc.total_hours + (day.total_hours || 0)
//         }
//       }, { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0, total_hours: 0 })
//       
//       userOBTotals[user.value.id] = totals
//     } else {
//       // No data, set zeros
//       userOBTotals[user.value.id] = { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0, total_hours: 0 }
//     }
//     
//     monthlyOBHours.value = userOBTotals
//   } catch (error) {
//     console.error('Failed to load monthly OB hours:', error)
//     // Don't show error to user as this is supplementary data
//     monthlyOBHours.value = {
//       [user.value.id]: { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0, total_hours: 0 }
//     }
//   }
// }

// OLD FUNCTION - Assigned hours loading now handled by centralized payroll store
// async function loadMonthlyAssignedHours() {
//   try {
//     if (!user.value?.id) return
//     
//     // Use the current date from shiftStore (set by TimeReporting)
//     const currentDate = shiftStore.currentDate || new Date()
//     const year = currentDate.getFullYear()
//     const month = currentDate.getMonth() // JavaScript months are 0-indexed
//     
//     // Calculate start and end dates for the selected month
//     const startDate = new Date(year, month, 1)
//     const endDate = new Date(year, month + 1, 0) // Last day of month
//     
//     // Get worker's assigned shifts for the month
//     const shifts = await shiftApi.getWorkerSchedule(user.value.id, startDate, endDate)
//     
//     // Calculate total assigned hours (only approved shifts count toward salary)
//     let totalHours = 0
//     
//     if (shifts && Array.isArray(shifts)) {
//       shifts.forEach(shift => {
//         // Only count approved shifts for salary calculation
//         // Status is in shift.assignment.status, default to 'approved' if not set
//         const status = shift.assignment?.status || 'approved'
//         if (status === 'approved') {
//           const startTime = new Date(shift.start_time)
//           const endTime = new Date(shift.end_time)
//           const durationMs = endTime - startTime
//           let durationHours = durationMs / (1000 * 60 * 60)
//           
//           // Apply same break logic as time entries:
//           // If shift is 5.5+ hours, subtract 0.5 hours break
//           if (durationHours >= 5.5) {
//             durationHours -= 0.5
//           }
//           
//           totalHours += durationHours
//         }
//       })
//     }
//     
//     monthlyAssignedHours.value = totalHours
//   } catch (error) {
//     console.error('Failed to load monthly assigned hours:', error)
//     monthlyAssignedHours.value = 0
//   }
// }

// OLD FUNCTION - Extra hours loading now handled by centralized payroll store
// async function loadMonthlyExtraHours() {
//   - removed for brevity, extra hours calculation is now centralized in payroll store
// }

// OLD FUNCTION - Sick leaves loading now handled by centralized payroll store
// async function loadMonthlySickLeaves() {
//   - removed for brevity, sick leave data and calculations are now centralized in payroll store
// }
</script>

<style scoped>
.dashboard-container {
  max-width: 800px;
  margin: 1rem auto;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  color: #0a2544;
}

.user-info {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  color: inherit;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
  color: inherit;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.info-item label {
  font-weight: bold;
  color: inherit;
}

.role-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: bold;
  text-transform: capitalize;
}

.role-badge.worker {
  background: #e3f2fd;
  color: #1976d2;
}

.role-badge.manager {
  background: #f3e5f5;
  color: #7b1fa2;
}

.loading, .error {
  padding: 1rem;
  text-align: center;
  border-radius: 8px;
}

.loading {
  background: #f0f0f0;
  color: #666;
}

.error {
  background: #ffebee;
  color: #c62828;
}

@media screen and (max-width: 768px) {
  .dashboard-container {
    display: none;
  }
}

@media screen and (max-width: 600px) {
  .info-column {
    display: none;
  }
}

.payment-summary {
  margin-top: 2rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #0a2544;
}

.payment-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.payment-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.breakdown {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  
}

.payment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.payment-card.highlighted {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  transform: scale(1.02);
}

.payment-card.highlighted:hover {
  transform: scale(1.02) translateY(-2px);
  box-shadow: 0 8px 24px rgba(33, 150, 243, 0.3);
}

.payment-card h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 500;
  opacity: 0.8;
}

.payment-card.highlighted h3 {
  opacity: 0.9;
}

.payment-value {
  font-size: 2rem;
  font-weight: 700;
  margin: 0.5rem 0;
  color: #0a2544;
}

.payment-card.highlighted .payment-value {
  color: white;
}

.payment-breakdown {
  font-size: 0.875rem;
  opacity: 0.7;
  line-height: 1.4;
}

.payment-card.highlighted .payment-breakdown {
  opacity: 0.9;
}

.payment-breakdown-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

@media screen and (max-width: 768px) {
  .payment-cards {
    grid-template-columns: 1fr;
  }
  
  .payment-card {
    padding: 1rem;
  }
  
  .payment-value {
    font-size: 1.5rem;
  }
}
</style>
